import { useQuery } from '@apollo/client';
import { Box } from '@mui/material';
import { Plus } from '@styled-icons/bootstrap';
import Loader from 'Components/Forms/Loader';
import SearchToolbar from 'Components/Forms/SearchToolbar';
import { EActivitiesTableColumnKeys } from 'FeatureModules/Activity/Components/ActivitiesTable/activitiesHeaderColumns';
import ActivitiesTable from 'FeatureModules/Activity/Components/ActivitiesTable/ActivitiesTable';
import PartnerSkeleton from 'FeatureModules/Partner/Components/Skeleton/PartnerSkeleton';
import { CardIllustration, EmptyStateCard, ThemeButton } from 'DesignSystem';
import Icon from 'DesignSystem/Primitives/Icon/Icon';
import { GET_SITE } from 'graphql/secure/queries/site.queries';
import useActivities from 'FeatureModules/Activity/hooks/useActivities';
import React, { FC } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  EBackgroundTypes,
  EIllustrationTypes,
  EItemTypes,
} from 'DesignSystem/types/background.types';
import { EAvailableSizes } from 'DesignSystem/types/size.types';
import { TRouteParamsWithId } from 'types/router.types';
import { ISite } from 'FeatureModules/Site/types/site.types';
import ActionContainer from 'Components/ActionContainer';
import EmptySearchResults from 'Components/EmptySearchResults';
import ErrorState from 'Components/ErrorState';
import InfiniteScrollWrapper from 'Components/InfiniteScrollWrapper';
import PageContainer from 'Components/PageContainer';
import PageTitle from 'Components/PageTitle';

const SiteActivities: FC = () => {
  const { id: siteId } = useParams<TRouteParamsWithId>();

  const { data: siteData } = useQuery<{ site: ISite }>(GET_SITE, {
    variables: {
      id: siteId,
    },
    skip: !siteId,
  });

  const navigate = useNavigate();
  const {
    activities,
    loading,
    error,
    searchTerm,
    hasNextPage,
    handlePageEndReached,
    handleSearchTermChange,
    handleCreateActivity,
    handleEditActivity,
  } = useActivities({
    defaultSiteId: siteId,
    additionalAddEditDialogProps: { site: siteData?.site },
  });

  if (error) {
    return <ErrorState />;
  }

  if (!activities.length && !searchTerm && !loading) {
    return (
      <PageContainer>
        <PageTitle
          title="Site activities"
          goBackLabel="All sites"
          titleExtension={siteData?.site?.title}
          goBackUrl="/sites"
        />
        <Box mb={3} />

        <EmptyStateCard
          title="Use your site on activities in your chains to map your supply chain and promote transparency"
          variant={EItemTypes.COMPONENT_CHAIN}
          buttonIcon={<Icon name="component-chain" color="white" />}
          buttonText="Create activity"
          onCreateItemClick={handleCreateActivity}
          background={EBackgroundTypes.WORLD}
          size={EAvailableSizes.MEDIUM}
          renderIllustration={
            <CardIllustration image={EIllustrationTypes.LOCATIONS} width={300} right={25} top={0} />
          }
        />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageTitle
        title="Site activities"
        goBackLabel="All Sites"
        goBackUrl="/sites"
        titleExtension={siteData?.site?.title}
      />
      <Box mt={2} position="relative">
        <ActionContainer>
          <SearchToolbar
            setSearchTerm={handleSearchTermChange}
            searchTerm={searchTerm || ''}
            className="search-input"
            data-testid="activity-search-input"
            placeholder="Search activity"
            actions={
              <Box ml={1}>
                <ThemeButton
                  size="medium"
                  color="PRIMARY"
                  onClick={handleCreateActivity}
                  startIcon={<Plus size={20} color="white" />}
                  data-testid="create-activity-btn"
                >
                  Create activity
                </ThemeButton>
              </Box>
            }
          />
        </ActionContainer>

        {loading && (
          <Box position="relative" mt={20}>
            <Loader />
          </Box>
        )}

        {activities.length > 0 && !loading && (
          <InfiniteScrollWrapper hasMore={hasNextPage} next={handlePageEndReached}>
            <ActivitiesTable
              columns={[
                EActivitiesTableColumnKeys.TITLE,
                EActivitiesTableColumnKeys.MODIFIED_TIMESTAMP,
                EActivitiesTableColumnKeys.ACTIONS,
              ]}
              activities={activities}
              onSelect={({ id }) => navigate(`/activities/${id}`)}
              onEdit={handleEditActivity}
            />
          </InfiniteScrollWrapper>
        )}
      </Box>
      {!activities.length && !loading && (
        <EmptySearchResults
          message="No search results found"
          action={{
            label: 'ADd new activity',
            onClick: handleCreateActivity,
          }}
          skeletonComponent={<PartnerSkeleton title={searchTerm || ''} />}
        />
      )}
    </PageContainer>
  );
};

export default SiteActivities;
